import React, { useState } from 'react';
import { 
  Activity, AlertTriangle, Brain, Heart, Shield, 
  CheckCircle, XCircle, Clock, TrendingUp, TrendingDown,
  User, Calendar, FileText, Download, Eye, Edit3, Plus
} from 'lucide-react';

const AssessmentTools = ({ onClose }) => {
  const [activeAssessment, setActiveAssessment] = useState('falls-risk');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedResident, setSelectedResident] = useState(null);
  const [showNewAssessment, setShowNewAssessment] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notifications, setNotifications] = useState([]);

  // Mock residents data
  const mockResidents = [
    { id: 'RES001', name: 'Alice Smith', room: 'A101', age: 78, gender: 'Female' },
    { id: 'RES002', name: 'Bob Johnson', room: 'A102', age: 82, gender: 'Male' },
    { id: 'RES003', name: 'Charlie Brown', room: 'B201', age: 75, gender: 'Male' },
    { id: 'RES004', name: 'Diana Prince', room: 'B202', age: 85, gender: 'Female' },
    { id: 'RES005', name: 'Eve Adams', room: 'C301', age: 79, gender: 'Female' },
  ];

  // Mock assessment data
  const assessmentData = {
    'falls-risk': {
      title: 'Falls Risk Assessment',
      icon: <AlertTriangle className="w-6 h-6 text-red-600" />,
      color: 'red',
      assessments: [
        {
          id: 'FRA001',
          resident: 'Alice Smith',
          date: '2024-07-20',
          score: 85,
          riskLevel: 'High',
          factors: ['Previous falls', 'Medication side effects', 'Mobility issues'],
          recommendations: ['Bed alarm', 'Regular mobility assessment', 'Medication review'],
          assessor: 'Dr. Jane Smith',
          status: 'Active'
        },
        {
          id: 'FRA002',
          resident: 'Bob Johnson',
          date: '2024-07-18',
          score: 45,
          riskLevel: 'Medium',
          factors: ['Age-related changes', 'Vision impairment'],
          recommendations: ['Regular eye checks', 'Assistive devices'],
          assessor: 'Nurse Emily',
          status: 'Active'
        },
        {
          id: 'FRA003',
          resident: 'Charlie Brown',
          date: '2024-07-15',
          score: 25,
          riskLevel: 'Low',
          factors: ['Good mobility'],
          recommendations: ['Maintain current care plan'],
          assessor: 'Dr. Alex',
          status: 'Completed'
        }
      ]
    },
    'pressure-injury': {
      title: 'Pressure Injury Risk Assessment',
      icon: <Shield className="w-6 h-6 text-orange-600" />,
      color: 'orange',
      assessments: [
        {
          id: 'PIRA001',
          resident: 'Diana Prince',
          date: '2024-07-19',
          score: 78,
          riskLevel: 'High',
          factors: ['Immobility', 'Poor nutrition', 'Moisture'],
          recommendations: ['Pressure relief mattress', 'Regular repositioning', 'Nutritional support'],
          assessor: 'Nurse Sarah',
          status: 'Active'
        },
        {
          id: 'PIRA002',
          resident: 'Eve Adams',
          date: '2024-07-17',
          score: 35,
          riskLevel: 'Low',
          factors: ['Good mobility'],
          recommendations: ['Regular skin checks'],
          assessor: 'Dr. David',
          status: 'Active'
        }
      ]
    },
    'nutrition': {
      title: 'Nutrition Assessment',
      icon: <Heart className="w-6 h-6 text-green-600" />,
      color: 'green',
      assessments: [
        {
          id: 'NA001',
          resident: 'Alice Smith',
          date: '2024-07-20',
          score: 65,
          riskLevel: 'Medium',
          factors: ['Weight loss', 'Poor appetite'],
          recommendations: ['Nutritional supplements', 'Dietary consultation'],
          assessor: 'Dietitian Mary',
          status: 'Active'
        },
        {
          id: 'NA002',
          resident: 'Bob Johnson',
          date: '2024-07-18',
          score: 85,
          riskLevel: 'Low',
          factors: ['Good appetite'],
          recommendations: ['Maintain current diet'],
          assessor: 'Dietitian Mary',
          status: 'Completed'
        }
      ]
    },
    'cognitive': {
      title: 'Cognitive Assessment',
      icon: <Brain className="w-6 h-6 text-blue-600" />,
      color: 'blue',
      assessments: [
        {
          id: 'CA001',
          resident: 'Charlie Brown',
          date: '2024-07-19',
          score: 42,
          riskLevel: 'High',
          factors: ['Memory impairment', 'Confusion', 'Disorientation'],
          recommendations: ['Memory aids', 'Structured routine', 'Family support'],
          assessor: 'Dr. Lisa',
          status: 'Active'
        },
        {
          id: 'CA002',
          resident: 'Diana Prince',
          date: '2024-07-16',
          score: 75,
          riskLevel: 'Low',
          factors: ['Mild forgetfulness'],
          recommendations: ['Regular cognitive exercises'],
          assessor: 'Dr. Lisa',
          status: 'Active'
        }
      ]
    }
  };

  const filteredResidents = mockResidents.filter(resident =>
    resident.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    resident.room.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const showNotification = (message, type) => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 5000);
  };

  const getRiskColor = (riskLevel) => {
    switch (riskLevel) {
      case 'High': return 'text-red-600 bg-red-100';
      case 'Medium': return 'text-orange-600 bg-orange-100';
      case 'Low': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getScoreColor = (score) => {
    if (score >= 70) return 'text-red-600';
    if (score >= 40) return 'text-orange-600';
    return 'text-green-600';
  };

  const handleNewAssessment = () => {
    setShowNewAssessment(true);
    showNotification('Starting new assessment...', 'info');
  };

  const handleViewAssessment = (assessment) => {
    console.log('Viewing assessment:', assessment.id);
    showNotification(`Viewing ${assessment.id} assessment`, 'success');
  };

  const handleEditAssessment = (assessment) => {
    console.log('Editing assessment:', assessment.id);
    showNotification(`Editing ${assessment.id} assessment`, 'info');
  };

  const handleDeleteAssessment = (assessment) => {
    console.log('Deleting assessment:', assessment.id);
    showNotification(`${assessment.id} assessment deleted`, 'success');
  };

  const currentAssessments = assessmentData[activeAssessment]?.assessments || [];

  if (!onClose) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-6 max-w-7xl w-full mx-4 max-h-[90vh] overflow-y-auto shadow-xl">
        {/* Notifications */}
        <div className="fixed top-4 right-4 z-[100]">
          {notifications.map(notification => (
            <div
              key={notification.id}
              className={`p-4 mb-2 rounded-lg shadow-md text-white flex items-center gap-3 ${
                notification.type === 'success' ? 'bg-green-500' :
                notification.type === 'error' ? 'bg-red-500' : 'bg-blue-500'
              }`}
            >
              {notification.type === 'success' && <CheckCircle className="w-5 h-5" />}
              {notification.type === 'error' && <XCircle className="w-5 h-5" />}
              {notification.type === 'info' && <FileText className="w-5 h-5" />}
              <span>{notification.message}</span>
            </div>
          ))}
        </div>

        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-3">
            <Activity className="w-7 h-7 text-indigo-600" />
            Clinical Assessment Tools
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ×
          </button>
        </div>

        {/* Assessment Type Tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          {Object.entries(assessmentData).map(([key, data]) => (
            <button
              key={key}
              onClick={() => setActiveAssessment(key)}
              className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                activeAssessment === key
                  ? `bg-${data.color}-600 text-white`
                  : `bg-${data.color}-100 text-${data.color}-700 hover:bg-${data.color}-200`
              }`}
            >
              {data.icon}
              {data.title}
            </button>
          ))}
        </div>

        {/* Search and Actions */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search residents..."
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <button
            onClick={handleNewAssessment}
            className="bg-indigo-600 text-white px-6 py-3 rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            New Assessment
          </button>
        </div>

        {/* Assessment Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Assessments</p>
                <h3 className="text-2xl font-bold text-gray-900">{currentAssessments.length}</h3>
              </div>
              <FileText className="w-8 h-8 text-indigo-400" />
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">High Risk</p>
                <h3 className="text-2xl font-bold text-red-600">
                  {currentAssessments.filter(a => a.riskLevel === 'High').length}
                </h3>
              </div>
              <AlertTriangle className="w-8 h-8 text-red-400" />
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Active</p>
                <h3 className="text-2xl font-bold text-green-600">
                  {currentAssessments.filter(a => a.status === 'Active').length}
                </h3>
              </div>
              <CheckCircle className="w-8 h-8 text-green-400" />
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Completed</p>
                <h3 className="text-2xl font-bold text-blue-600">
                  {currentAssessments.filter(a => a.status === 'Completed').length}
                </h3>
              </div>
              <Clock className="w-8 h-8 text-blue-400" />
            </div>
          </div>
        </div>

        {/* Assessment List */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
              {assessmentData[activeAssessment]?.icon}
              {assessmentData[activeAssessment]?.title} - Recent Assessments
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Resident</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Score</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Risk Level</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Assessor</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currentAssessments.map((assessment) => (
                  <tr key={assessment.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {assessment.resident}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {assessment.date}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={`font-semibold ${getScoreColor(assessment.score)}`}>
                        {assessment.score}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getRiskColor(assessment.riskLevel)}`}>
                        {assessment.riskLevel}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {assessment.assessor}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                        assessment.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                      }`}>
                        {assessment.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleViewAssessment(assessment)}
                          className="text-blue-600 hover:text-blue-900"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleEditAssessment(assessment)}
                          className="text-indigo-600 hover:text-indigo-900"
                          title="Edit Assessment"
                        >
                          <Edit3 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteAssessment(assessment)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete Assessment"
                        >
                          <XCircle className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* New Assessment Modal */}
        {showNewAssessment && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60">
            <div className="bg-white rounded-2xl p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-gray-800">
                  New {assessmentData[activeAssessment]?.title}
                </h3>
                <button
                  onClick={() => setShowNewAssessment(false)}
                  className="text-gray-500 hover:text-gray-700 text-2xl"
                >
                  ×
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Select Resident</label>
                  <select className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500">
                    <option value="">Choose a resident...</option>
                    {filteredResidents.map(resident => (
                      <option key={resident.id} value={resident.id}>
                        {resident.name} ({resident.room}) - {resident.age}y {resident.gender}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Assessment Date</label>
                    <input
                      type="date"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      defaultValue={new Date().toISOString().split('T')[0]}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Assessor</label>
                    <input
                      type="text"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="Enter assessor name"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Assessment Notes</label>
                  <textarea
                    rows={4}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Enter assessment details and observations..."
                  />
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <button
                    onClick={() => setShowNewAssessment(false)}
                    className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      setShowNewAssessment(false);
                      showNotification('New assessment created successfully!', 'success');
                    }}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                  >
                    Create Assessment
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AssessmentTools;
